<script lang="ts">
  import Breadcrumb from '../shared/Breadcrumb.svelte';
  import EnvSwitcher from '../shared/EnvSwitcher.svelte';
  import { fetchQueueStats, type QueueStats } from '../../services/sqs-client';

  let env = $state<'Beta' | 'Prod'>('Beta');
  let trigger = $state<QueueStats | null>(null);
  let dlq = $state<QueueStats | null>(null);
  let loading = $state(false);
  let lastRefresh = $state('');
  let autoRefresh = $state(false);
  let refreshTimer = $state<ReturnType<typeof setInterval> | null>(null);

  let error = $state('');

  async function refresh() {
    loading = true;
    try {
      const data = await fetchQueueStats(env);
      trigger = data.trigger;
      dlq = data.dlq;
      error = '';
    } catch (e: any) {
      error = e.message || 'Failed to fetch queue stats';
    }
    lastRefresh = new Date().toLocaleTimeString();
    loading = false;
  }

  function startAutoRefresh() {
    stopAutoRefresh();
    refresh();
    refreshTimer = setInterval(refresh, 10_000);
  }

  function stopAutoRefresh() {
    if (refreshTimer) {
      clearInterval(refreshTimer);
      refreshTimer = null;
    }
  }

  function toggleAutoRefresh() {
    if (autoRefresh) startAutoRefresh();
    else stopAutoRefresh();
  }

  $effect(() => {
    // Clean up on env change
    env;
    stopAutoRefresh();
    trigger = null;
    dlq = null;
    autoRefresh = false;
    return stopAutoRefresh;
  });

  function formatAge(seconds: number): string {
    if (seconds === 0) return '—';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  }
</script>

<div class="min-h-screen bg-bg-dark text-text-primary">
  <div class="w-full px-6 py-4">
    <div class="flex items-center justify-between pr-20">
      <Breadcrumb items={[{ label: 'SQS Monitor' }]} />
      <div class="flex items-center gap-4">
        <div class="flex items-center gap-2 text-xs text-text-muted">
          <label class="inline-flex items-center cursor-pointer">
            <input type="checkbox" bind:checked={autoRefresh} onchange={toggleAutoRefresh} class="sr-only peer" />
            <div class="relative w-9 h-5 bg-bg-input rounded-full peer peer-focus:ring-2 peer-focus:ring-accent/40 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-text-muted after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-accent"></div>
            <span class="ms-2 text-xs text-text-muted select-none">Auto-refresh (10s)</span>
          </label>
          {#if lastRefresh}
            <span>· Last: {lastRefresh}</span>
          {/if}
          <button onclick={refresh} disabled={loading} class="px-2.5 py-1 bg-bg-input border border-border hover:border-accent/50 text-text-secondary text-xs rounded-lg transition-colors disabled:opacity-50">
            {loading ? '⟳' : '↻'} Refresh
          </button>
        </div>
        <EnvSwitcher bind:value={env} />
      </div>
    </div>

    <div class="mt-4 mb-6">
      <h1 class="text-2xl font-bold">📨 SQS Monitor</h1>
      <p class="mt-1 text-sm text-text-secondary">Real-time queue depth and DLQ monitoring</p>
    </div>

    {#if loading}
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        {#each [1, 2] as _ (_.toString())}
          <div class="h-64 rounded-xl bg-bg-panel animate-skeleton-pulse"></div>
        {/each}
      </div>
    {:else if !trigger && !dlq && !error}
      <div class="bg-bg-panel border border-border rounded-xl p-12 text-center">
        <p class="text-4xl mb-3">📨</p>
        <p class="text-text-secondary mb-4">Click to load queue stats</p>
        <button onclick={refresh} class="px-5 py-2 bg-accent hover:bg-accent-hover text-white rounded-lg text-sm font-medium transition-colors">
          Refresh Now
        </button>
      </div>
    {:else if error}
      <div class="bg-error/10 border border-error/30 rounded-xl p-6 text-center">
        <p class="text-error font-medium">Failed to load queue stats</p>
        <p class="text-xs text-text-muted mt-1">{error}</p>
      </div>
    {:else}
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Trigger Queue -->
        {#if trigger}
          <div class="bg-bg-panel border border-border rounded-xl p-6">
            <div class="flex items-center gap-3 mb-6">
              <div class="w-10 h-10 rounded-lg bg-accent/15 flex items-center justify-center text-lg">📥</div>
              <div>
                <h2 class="text-lg font-semibold">Trigger Queue</h2>
                <p class="text-xs text-text-muted font-mono truncate max-w-[300px]">{trigger.name}</p>
              </div>
            </div>
            <div class="grid grid-cols-3 gap-4">
              <div class="bg-bg-dark/50 rounded-lg p-4 text-center">
                <p class="text-3xl font-bold text-accent">{trigger.messagesAvailable}</p>
                <p class="text-xs text-text-secondary mt-1">Available</p>
              </div>
              <div class="bg-bg-dark/50 rounded-lg p-4 text-center">
                <p class="text-3xl font-bold text-warning">{trigger.messagesInFlight}</p>
                <p class="text-xs text-text-secondary mt-1">In-Flight</p>
              </div>
              <div class="bg-bg-dark/50 rounded-lg p-4 text-center">
                <p class="text-3xl font-bold text-text-primary">{formatAge(trigger.oldestMessageAge)}</p>
                <p class="text-xs text-text-secondary mt-1">Oldest Msg</p>
              </div>
            </div>
          </div>
        {/if}

        <!-- DLQ -->
        {#if dlq}
          {@const isDanger = dlq.messagesAvailable > 0}
          <div class="rounded-xl p-6 transition-colors {isDanger ? 'bg-error/10 border-2 border-error/50 animate-glow-border' : 'bg-bg-panel border border-border'}" style={isDanger ? '--theme-accent: var(--theme-error); --theme-accent-hover: var(--theme-error); --theme-accent-glow: rgba(248,113,113,0.3)' : ''}>
            <div class="flex items-center gap-3 mb-6">
              <div class="w-10 h-10 rounded-lg {isDanger ? 'bg-error/20' : 'bg-success/15'} flex items-center justify-center text-lg">
                {isDanger ? '🚨' : '✅'}
              </div>
              <div>
                <h2 class="text-lg font-semibold">Dead Letter Queue</h2>
                <p class="text-xs text-text-muted font-mono truncate max-w-[300px]">{dlq.name}</p>
              </div>
              {#if isDanger}
                <span class="ml-auto px-2.5 py-1 rounded-full text-xs font-semibold bg-error/20 text-error border border-error/30">
                  ⚠ Messages in DLQ
                </span>
              {/if}
            </div>
            <div class="grid grid-cols-3 gap-4">
              <div class="bg-bg-dark/50 rounded-lg p-4 text-center">
                <p class="text-3xl font-bold {isDanger ? 'text-error' : 'text-success'}">{dlq.messagesAvailable}</p>
                <p class="text-xs text-text-secondary mt-1">Available</p>
              </div>
              <div class="bg-bg-dark/50 rounded-lg p-4 text-center">
                <p class="text-3xl font-bold text-text-primary">{dlq.messagesInFlight}</p>
                <p class="text-xs text-text-secondary mt-1">In-Flight</p>
              </div>
              <div class="bg-bg-dark/50 rounded-lg p-4 text-center">
                <p class="text-3xl font-bold text-text-primary">{formatAge(dlq.oldestMessageAge)}</p>
                <p class="text-xs text-text-secondary mt-1">Oldest Msg</p>
              </div>
            </div>
          </div>
        {/if}
      </div>
    {/if}
  </div>
</div>
